// Languages settings — placeholder M3 (admin + sellers + vendors).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #10):
// UI language preference radio list. i18n delivery + persistence ratify M3
// Stage 4 SPEC.md Faz 6 (workspace + resources expansion — i18n binding).
//
// Wave-1 Day 3 self-wire: locale switcher kendi UI'sını anında çevirir.

import { Check, Globe } from 'lucide-react'
import { useT } from '../../i18n'
import { languagesI18n } from '../../i18n/settings/languages'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { useToastStore } from '../../lib/toast'
import { useLocaleStore, type Locale } from '../../store/locale'
import type { SettingsSurface } from './Settings'

// Real-mode: persist the chosen locale to the user's account (PATCH /auth/me).
// Fire-and-forget — the localStorage switch already drives the UI; backend
// persistence only matters for cross-device continuity. Failures are silent.
function persistLocale(locale: Locale): void {
  if (!USE_REAL_API) return
  void fetch(apiUrl('/auth/me'), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locale }),
  }).catch(() => {})
}

type LanguageOption = {
  code: string
  label: string
  description: string
  ready: boolean
}

const LOCALE_CODES = new Set<Locale>(['en', 'tr'])

export function Languages({ surface }: { surface: SettingsSurface }) {
  void surface
  const t = useT(languagesI18n)
  const push = useToastStore((s) => s.push)
  const selected = useLocaleStore((s) => s.locale)
  const setLocale = useLocaleStore((s) => s.setLocale)

  const options: LanguageOption[] = [
    {
      code: 'en',
      label: t.englishLabel,
      description: t.englishDescription,
      ready: true,
    },
    {
      code: 'tr',
      label: t.turkishLabel,
      description: t.turkishDescription,
      ready: true,
    },
    {
      code: 'de',
      label: t.germanLabel,
      description: t.germanDescription,
      ready: false,
    },
  ]

  const select = (code: string, label: string, ready: boolean) => {
    if (!ready) {
      push({
        variant: 'warning',
        title: `${label} ${t.comingInM3Suffix}`,
        description: t.m3UnavailableDescription,
        duration_ms: 4000,
      })
      return
    }
    if (LOCALE_CODES.has(code as Locale)) {
      setLocale(code as Locale)
      persistLocale(code as Locale)
    }
    push({
      variant: 'info',
      title: t.toastTitle,
      description: t.toastDescription,
      duration_ms: 3000,
    })
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.pageDescription}</p>
      </header>

      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
            <Globe className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t.sectionTitle}
            </h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              {t.sectionDescription}
            </p>
          </div>
        </header>
        <ul className="divide-y divide-[var(--color-border)]">
          {options.map((lang) => {
            const isSelected = selected === lang.code
            return (
              <li key={lang.code}>
                <button
                  type="button"
                  onClick={() => select(lang.code, lang.label, lang.ready)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={
                        'flex h-4 w-4 items-center justify-center rounded-full border ' +
                        (isSelected
                          ? 'border-brand-600 bg-brand-600'
                          : 'border-[var(--color-border-strong)] bg-[var(--color-surface)]')
                      }
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text)]">
                        {lang.label}
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                        {lang.description}
                      </p>
                    </div>
                  </div>
                  {!lang.ready && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                      {t.m3Badge}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </section>

      <p className="text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>
    </div>
  )
}
